import { useCallback, useReducer, useRef } from "react";
import { Link } from "react-router-dom";
import type {
  FogbugzBugBarResult,
  FogbugzToAdoCaseBundle,
  FogbugzToAdoCreateResult,
  FogbugzToAdoDraft,
  FogbugzToAdoPmAddition,
  FogbugzToAdoRefusal,
  FogbugzToAdoReporterQuestion,
  FogbugzToAdoSlotVerdict,
  FogbugzToAdoToolsetBridge,
} from "@ttc-spectree/toolbox-contract";
import { Chip } from "@/components/grooming/grooming-chips";
import { OpenInToolboxCallout } from "@/components/toolbox/open-in-toolbox-callout";
import { BarStep } from "@/components/toolbox/fogbugz-to-ado/bar-step";
import { BelowBarStep } from "@/components/toolbox/fogbugz-to-ado/below-bar-step";
import { CaseStep, RefusalCard } from "@/components/toolbox/fogbugz-to-ado/case-step";
import { ClaudeRun, EngineNotice, Notice, Waiting, errorText } from "@/components/toolbox/fogbugz-to-ado/parts";
import { CreatedResult, ReviewStep } from "@/components/toolbox/fogbugz-to-ado/review-step";
import {
  useCreatingFogbugzToAdoBug,
  useFogbugzToAdoBarCheck,
  usePrepareFogbugzCase,
} from "@/hooks/queries/use-fogbugz-to-ado";
import { useMe } from "@/hooks/queries/use-me";
import { usePageTitle } from "@/hooks/use-page-title";
import { loadCaseImages, type CaseImages } from "@/lib/api/fogbugz-to-ado";
import { useFogbugzToAdoBridge, useToolboxBridge, useToolsetSupport } from "@/lib/toolbox/bridge";
import { useClaudeRunOutcome } from "@/lib/toolbox/claude-signals";
import {
  fogbugzToAdoEngineMessage,
  hasFogbugzToAdoBridge,
  orderedVerdicts,
  verdictsFromIntent,
  type FogbugzToAdoEngineMessage,
} from "@/lib/toolbox/fogbugz-to-ado";
import { FOGBUGZ_TO_ADO_TOOL_NAME, TOOLBOX_HOME_PATH } from "@/lib/toolbox/tools";
import { TOOLBOX_PAGE_TITLE } from "@/lib/toolbox/type-scale";
import { SUPPORT_LABELS } from "@/pages/toolbox/home";

// ---------------------------------------------------------------------------
// Step state (exported for tests)
// ---------------------------------------------------------------------------

/** The slots the PM confirmed and what they added. */
interface Confirmed {
  slots: FogbugzToAdoSlotVerdict[];
  pmAdditions: FogbugzToAdoPmAddition[];
}

/** Where one case is. Every step after `case`/`preparing`/`refused`/`prepare-failed` has a bundle. */
export type FogbugzToAdoStep =
  | { step: "case" }
  | { step: "preparing" }
  | { step: "refused"; refusal: FogbugzToAdoRefusal }
  | { step: "prepare-failed"; message: string }
  | { step: "repos" }
  | { step: "repos-failed"; message: FogbugzToAdoEngineMessage }
  | { step: "images" }
  | { step: "reading" }
  | { step: "slots-failed"; message: FogbugzToAdoEngineMessage }
  | {
      step: "confirm";
      source: "intent" | "claude";
      verdicts: FogbugzToAdoSlotVerdict[];
      claudeQuestions: FogbugzToAdoReporterQuestion[];
      bar: FogbugzBugBarResult | null;
      checking: boolean;
      checkError: string | null;
    }
  | ({ step: "below-bar"; bar: FogbugzBugBarResult; claudeQuestions: FogbugzToAdoReporterQuestion[] } & Confirmed)
  | { step: "asked"; questions: FogbugzToAdoReporterQuestion[] }
  | ({ step: "drafting" } & Confirmed)
  | ({ step: "draft-failed"; message: FogbugzToAdoEngineMessage } & Confirmed)
  | ({ step: "review"; draft: FogbugzToAdoDraft } & Confirmed)
  | { step: "created"; result: FogbugzToAdoCreateResult };

export interface FogbugzToAdoFlowState {
  caseId: number | null;
  bundle: FogbugzToAdoCaseBundle | null;
  current: FogbugzToAdoStep;
}

export type FogbugzToAdoAction =
  | { type: "load"; caseId: number }
  | { type: "refused"; refusal: FogbugzToAdoRefusal }
  | { type: "prepare-failed"; message: string }
  | { type: "ready"; bundle: FogbugzToAdoCaseBundle }
  | { type: "step"; current: FogbugzToAdoStep };

export const INITIAL_FLOW: FogbugzToAdoFlowState = { caseId: null, bundle: null, current: { step: "case" } };

/** The page's step reducer. A new case clears everything before it. Pure; exported for tests. */
export function fogbugzToAdoReducer(state: FogbugzToAdoFlowState, action: FogbugzToAdoAction): FogbugzToAdoFlowState {
  switch (action.type) {
    case "load":
      return { caseId: action.caseId, bundle: null, current: { step: "preparing" } };
    case "refused":
      return { ...state, bundle: null, current: { step: "refused", refusal: action.refusal } };
    case "prepare-failed":
      return { ...state, bundle: null, current: { step: "prepare-failed", message: action.message } };
    case "ready":
      return { caseId: action.bundle.caseId, bundle: action.bundle, current: { step: "repos" } };
    case "step":
      return state.bundle ? { ...state, current: action.current } : state;
  }
}

/**
 * True while a SpecTree call or a Claude run is in flight (the case box
 * waits), the Bug create included (`creating`): loading another case then
 * would lose its result. Pure.
 */
export function flowBusy(current: FogbugzToAdoStep, creating = false): boolean {
  return (
    creating ||
    current.step === "preparing" ||
    current.step === "repos" ||
    current.step === "images" ||
    current.step === "reading" ||
    current.step === "drafting" ||
    (current.step === "confirm" && current.checking)
  );
}

// ---------------------------------------------------------------------------
// The flow
// ---------------------------------------------------------------------------

/**
 * Case number → prepare (refusal card, or the bundle) → repo copies on this
 * computer → the slots (from `intent.md`, or Claude reading the case with
 * every case image) → the Bug bar → below the bar, the PM's choice → Claude's
 * draft → review, area path and iteration → Create → result. SpecTree reads
 * and writes with the PM's own FogBugz key and ADO PAT; Claude runs on the
 * PM's own computer and never writes. The only writes are "Post to FogBugz
 * case N" and "Create ADO Bug", each one click.
 */
function FogbugzToAdoFlow({ fogbugz, pmName }: { fogbugz: FogbugzToAdoToolsetBridge; pmName: string }) {
  const [state, dispatch] = useReducer(fogbugzToAdoReducer, INITIAL_FLOW);
  const { caseId, bundle, current } = state;
  // Each case load gets a number; a result for an older number (another case loaded since) is ignored.
  const seq = useRef(0);
  const images = useRef<Promise<CaseImages> | null>(null);
  const reportClaudeRun = useClaudeRunOutcome();
  // Every SpecTree call goes through React Query; the create mutation lives in the review step.
  const { mutateAsync: prepareCase } = usePrepareFogbugzCase();
  const { mutateAsync: checkBar } = useFogbugzToAdoBarCheck();
  const creating = useCreatingFogbugzToAdoBug();

  const setStep = useCallback((at: number, next: FogbugzToAdoStep) => {
    if (at === seq.current) dispatch({ type: "step", current: next });
  }, []);

  /** Every image on the case for Claude, read once per case. */
  const caseImages = useCallback((ready: FogbugzToAdoCaseBundle): Promise<CaseImages> => {
    images.current ??= loadCaseImages(ready).catch(() => ({ images: [], skipped: [] }));
    return images.current;
  }, []);

  /** Bar step for a case with `intent.md`: its slots, and the bar SpecTree checked (or checks now). */
  const showIntent = useCallback(
    (at: number, ready: FogbugzToAdoCaseBundle) => {
      const verdicts = verdictsFromIntent(ready.intent);
      const base = { step: "confirm" as const, source: "intent" as const, verdicts, claudeQuestions: [] };
      if (ready.bar) {
        setStep(at, { ...base, bar: ready.bar, checking: false, checkError: null });
        return;
      }
      setStep(at, { ...base, bar: null, checking: true, checkError: null });
      checkBar({ caseId: ready.caseId, body: { slots: verdicts, pmAdditions: [] } })
        .then((bar) => {
          setStep(at, { ...base, bar, checking: false, checkError: null });
        })
        .catch((error: unknown) => {
          setStep(at, { ...base, bar: null, checking: false, checkError: errorText(error, "Could not check the Bug bar.") });
        });
    },
    [checkBar, setStep],
  );

  /** Bar step for a legacy case: Claude reads the case with the code open and every case image. */
  const readSlots = useCallback(
    async (at: number, ready: FogbugzToAdoCaseBundle) => {
      setStep(at, { step: "images" });
      const loaded = await caseImages(ready);
      if (at !== seq.current) return;
      setStep(at, { step: "reading" });
      let result: Awaited<ReturnType<FogbugzToAdoToolsetBridge["readSlots"]>>;
      try {
        result = await fogbugz.readSlots({ bundle: ready, images: loaded.images, pmName });
      } catch {
        setStep(at, { step: "slots-failed", message: fogbugzToAdoEngineMessage("UNKNOWN") });
        return;
      }
      reportClaudeRun(result);
      if (!result.ok) {
        setStep(at, { step: "slots-failed", message: fogbugzToAdoEngineMessage(result.errorCode, result.message) });
        return;
      }
      setStep(at, {
        step: "confirm",
        source: "claude",
        verdicts: orderedVerdicts(result.slots),
        claudeQuestions: result.reporterQuestions,
        bar: null,
        checking: false,
        checkError: null,
      });
    },
    [caseImages, fogbugz, pmName, reportClaudeRun, setStep],
  );

  /** The repo copies must be on this computer before any Claude run. */
  const checkRepos = useCallback(
    async (at: number, ready: FogbugzToAdoCaseBundle) => {
      let result: Awaited<ReturnType<FogbugzToAdoToolsetBridge["checkRepos"]>>;
      try {
        result = await fogbugz.checkRepos({ project: ready.ado.project, repos: ready.repos });
      } catch {
        setStep(at, { step: "repos-failed", message: fogbugzToAdoEngineMessage("UNKNOWN") });
        return;
      }
      if (at !== seq.current) return;
      if (!result.ok) {
        setStep(at, { step: "repos-failed", message: fogbugzToAdoEngineMessage(result.errorCode, result.message) });
        return;
      }
      // Only a Formulator intent.md carries slots; any other one (another source, cut off,
      // unreadable or not downloaded) is read by Claude like a case without one.
      if (ready.intent.slots !== undefined) {
        showIntent(at, ready);
        return;
      }
      await readSlots(at, ready);
    },
    [fogbugz, readSlots, setStep, showIntent],
  );

  const load = (id: number) => {
    seq.current += 1;
    const at = seq.current;
    images.current = null;
    dispatch({ type: "load", caseId: id });
    prepareCase(id)
      .then((response) => {
        if (at !== seq.current) return;
        if (response.status === "refused") {
          dispatch({ type: "refused", refusal: response.refusal });
          return;
        }
        dispatch({ type: "ready", bundle: response.bundle });
        void checkRepos(at, response.bundle);
      })
      .catch((error: unknown) => {
        if (at !== seq.current) return;
        dispatch({ type: "prepare-failed", message: errorText(error, `Could not read case #${String(id)}.`) });
      });
  };

  /** Claude drafts the Bug from the confirmed slots (only once the bar passes). */
  const draft = async (confirmed: Confirmed) => {
    if (!bundle) return;
    const at = seq.current;
    setStep(at, { step: "drafting", ...confirmed });
    const loaded = await caseImages(bundle);
    if (at !== seq.current) return;
    let result: Awaited<ReturnType<FogbugzToAdoToolsetBridge["draftBug"]>>;
    try {
      result = await fogbugz.draftBug({
        bundle,
        images: loaded.images,
        confirmedSlots: confirmed.slots,
        pmAdditions: confirmed.pmAdditions,
        pmName,
      });
    } catch {
      setStep(at, { step: "draft-failed", message: fogbugzToAdoEngineMessage("UNKNOWN"), ...confirmed });
      return;
    }
    reportClaudeRun(result);
    if (!result.ok) {
      setStep(at, { step: "draft-failed", message: fogbugzToAdoEngineMessage(result.errorCode, result.message), ...confirmed });
      return;
    }
    setStep(at, { step: "review", draft: result.draft, ...confirmed });
  };

  /** After the bar: draft when it passes, else the PM's choice. */
  const afterBar = (bar: FogbugzBugBarResult, confirmed: Confirmed, claudeQuestions: FogbugzToAdoReporterQuestion[]) => {
    if (bar.passes) {
      void draft(confirmed);
      return;
    }
    setStep(seq.current, { step: "below-bar", bar, claudeQuestions, ...confirmed });
  };

  /** Legacy case: the PM confirmed Claude's verdicts; only now is the bar checked. */
  const confirmLegacy = (confirmed: Confirmed) => {
    if (!bundle || current.step !== "confirm") return;
    const at = seq.current;
    const shown = current;
    setStep(at, { ...shown, checking: true, checkError: null });
    checkBar({ caseId: bundle.caseId, body: confirmed })
      .then((bar) => {
        if (at !== seq.current) return;
        afterBar(bar, confirmed, shown.claudeQuestions);
      })
      .catch((error: unknown) => {
        setStep(at, { ...shown, checking: false, checkError: errorText(error, "Could not check the Bug bar. Try again.") });
      });
  };

  const continueIntent = () => {
    if (current.step !== "confirm" || !current.bar) return;
    afterBar(current.bar, { slots: current.verdicts, pmAdditions: [] }, []);
  };

  const busy = flowBusy(current, creating);

  return (
    <div className="flex w-full max-w-5xl flex-col gap-5 px-4 pb-6 pt-7">
      <div className="flex flex-col gap-1">
        <h1 className={TOOLBOX_PAGE_TITLE}>{FOGBUGZ_TO_ADO_TOOL_NAME}</h1>
        <p className="text-[13px] text-muted-foreground">
          Bring a FogBugz bug case to the ADO board as a complete Bug. Your Claude reads the case with the product code
          open and drafts the Bug; you review it, pick where it goes, then create it as yourself.
        </p>
      </div>

      <CaseStep onLoad={load} busy={busy} />

      {caseId !== null && current.step === "preparing" && <Waiting label={`Reading case #${String(caseId)}`} />}
      {caseId !== null && current.step === "refused" && <RefusalCard caseId={caseId} refusal={current.refusal} />}
      {current.step === "prepare-failed" && (
        <Notice tone="error" testId="fogbugz-to-ado-prepare-error">
          <p>{current.message}</p>
        </Notice>
      )}

      {bundle && (
        <p className="text-[13px] text-muted-foreground" data-testid="fogbugz-to-ado-case-line">
          Case #{bundle.caseId}: <span className="text-foreground">{bundle.title}</span> · {bundle.fogbugzProject.name} →{" "}
          {bundle.ado.project}
        </p>
      )}

      {bundle && current.step === "repos" && <Waiting label="Checking the product code on this computer" />}
      {bundle && current.step === "repos-failed" && (
        <EngineNotice
          message={current.message}
          testId="fogbugz-to-ado-repos-error"
          onRetry={() => {
            seq.current += 1;
            dispatch({ type: "step", current: { step: "repos" } });
            void checkRepos(seq.current, bundle);
          }}
        />
      )}
      {bundle && current.step === "images" && <Waiting label="Getting the case's screenshots" />}
      {bundle && current.step === "reading" && (
        <ClaudeRun fogbugz={fogbugz} caseId={bundle.caseId} phase="reading-case" testId="fogbugz-to-ado-reading" />
      )}
      {bundle && current.step === "slots-failed" && (
        <EngineNotice
          message={current.message}
          testId="fogbugz-to-ado-slots-error"
          onRetry={() => {
            void readSlots(seq.current, bundle);
          }}
        />
      )}

      {bundle && current.step === "confirm" && (
        <BarStep
          caseId={bundle.caseId}
          source={current.source}
          verdicts={current.verdicts}
          bar={current.bar}
          checking={current.checking}
          checkError={current.checkError}
          onConfirm={confirmLegacy}
          onContinue={continueIntent}
        />
      )}

      {bundle && current.step === "below-bar" && (
        <BelowBarStep
          caseId={bundle.caseId}
          pmName={pmName}
          bar={current.bar}
          slots={current.slots}
          pmAdditions={current.pmAdditions}
          claudeQuestions={current.claudeQuestions}
          onPassed={(confirmed) => {
            void draft(confirmed);
          }}
          onPosted={(questions) => {
            setStep(seq.current, { step: "asked", questions });
          }}
        />
      )}

      {bundle && current.step === "asked" && (
        <Notice tone="ok" testId="fogbugz-to-ado-asked">
          <p>
            Posted {current.questions.length === 1 ? "1 question" : `${String(current.questions.length)} questions`} to
            FogBugz case {bundle.caseId}. Nothing was written to ADO.
          </p>
          <p>When the reporter answers, check the case again.</p>
        </Notice>
      )}

      {bundle && current.step === "drafting" && (
        <ClaudeRun fogbugz={fogbugz} caseId={bundle.caseId} phase="drafting-bug" testId="fogbugz-to-ado-drafting" />
      )}
      {bundle && current.step === "draft-failed" && (
        <EngineNotice
          message={current.message}
          testId="fogbugz-to-ado-draft-error"
          onRetry={() => {
            void draft({ slots: current.slots, pmAdditions: current.pmAdditions });
          }}
        />
      )}

      {bundle && current.step === "review" && (
        <ReviewStep
          bundle={bundle}
          pmName={pmName}
          draft={current.draft}
          slots={current.slots}
          pmAdditions={current.pmAdditions}
          onCreated={(result) => {
            setStep(seq.current, { step: "created", result });
          }}
        />
      )}

      {bundle && current.step === "created" && <CreatedResult caseId={bundle.caseId} result={current.result} />}
    </div>
  );
}

/**
 * `/toolbox/fogbugz-to-ado`: FogBugz to ADO in SpecTree Toolbox (epic
 * PI-SPEC-SW-E311, feature PI-SPEC-SW-1640, ER-1666 Package E, Resolved
 * Decisions 7, 7b, 8 and 9). A Project Manager brings an existing FogBugz
 * bug case to the ADO board as a complete Bug: everything in the case as
 * text, the original files, a link back to the case and the `fogbugz-<n>`
 * tag, then a note with the ADO link on the case. Routing is never
 * inferred: the area path starts from the FogBugz ↔ ADO link and the
 * iteration starts empty.
 *
 * Behind RequireFogbugzToolbox (the `fogbugz-to-ado` capability somewhere
 * and a saved FogBugz key) and RequireAdoPat. In a plain browser it shows how
 * to open SpecTree Toolbox; in an app without the toolset, or whose contract
 * SpecTree does not serve, the same update words as the home tile.
 */
export function ToolboxFogbugzToAdoPage() {
  usePageTitle(FOGBUGZ_TO_ADO_TOOL_NAME);
  const bridge = useToolboxBridge();
  const fogbugz = useFogbugzToAdoBridge();
  const support = useToolsetSupport("fogbugz-to-ado");
  const { data: me } = useMe();

  if (!bridge) {
    return (
      <div className="container mx-auto max-w-3xl px-4 py-6">
        <OpenInToolboxCallout reason="FogBugz to ADO runs in SpecTree Toolbox: your own Claude Code reads the case and drafts the Bug on your computer." />
      </div>
    );
  }

  if (support === undefined) {
    return (
      <div className="flex w-full px-4 pt-7">
        <Waiting label="Checking SpecTree Toolbox" />
      </div>
    );
  }

  if (support !== "supported" || !hasFogbugzToAdoBridge(fogbugz) || !fogbugz) {
    const label = SUPPORT_LABELS[support === "spectree-update-needed" ? "spectree-update-needed" : "app-update-needed"];
    return (
      <div className="flex w-full flex-col gap-3 px-4 pb-6 pt-7" data-testid="fogbugz-to-ado-update-needed">
        <h1 className={TOOLBOX_PAGE_TITLE}>{FOGBUGZ_TO_ADO_TOOL_NAME}</h1>
        <div>
          <Chip tone={label.tone}>{label.text}</Chip>
        </div>
        <Link to={TOOLBOX_HOME_PATH} className="text-[13px] text-link hover:underline">
          Back to SpecTree Toolbox
        </Link>
      </div>
    );
  }

  const name = me?.name.trim() ?? "";
  return <FogbugzToAdoFlow fogbugz={fogbugz} pmName={name !== "" ? name : "A Project Manager"} />;
}
